import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router";
import { ChevronRight, ImageOff } from "lucide-react";
import { ApiError } from "../../api/client";
import { catalogueApi } from "../../api/catalogue";
import { Breadcrumb } from "../../components/catalogue/Breadcrumb";
import { EmptyState, LoadError } from "../../components/catalogue/Notices";
import { PageWidth } from "../../components/catalogue/StoreShell";
import { Icon } from "../../components/ui";
import { NotFound } from "./NotFoundRoute";
import { usePageTitle } from "./usePageTitle";

/** PRD screen 3: one machine's diagrams, in the book's order. */
export function EquipmentModelRoute() {
  const id = Number(useParams().id);
  const machine = useQuery({ queryKey: ["catalogue", "machine", id], queryFn: () => catalogueApi.machine(id) });
  usePageTitle(machine.data?.name ?? null);

  if (machine.isError && machine.error instanceof ApiError && machine.error.status === 404) return <NotFound />;
  return (
    <PageWidth className="pt-6">
      {machine.isPending ? (
        <p className="type-body text-text-muted">Loading…</p>
      ) : machine.isError ? (
        <LoadError onRetry={() => machine.refetch()} />
      ) : (
        <>
          <Breadcrumb items={[{ label: machine.data.equipment_category.name, to: "/equipment-categories" }, { label: machine.data.name }]} />
          <h1 className="mt-4 mb-2 type-h1 [font-size:var(--text-2xl)]">{machine.data.name}</h1>
          <p className="mb-8 type-body text-text-muted">Choose the part of the machine. Its diagram numbers every part.</p>
          {machine.data.assemblies.length === 0 ? (
            <EmptyState icon={ImageOff} title="No diagrams for this machine yet">
              We're still adding them. <Link to="/equipment-categories">Browse other machines</Link>.
            </EmptyState>
          ) : (
            <ol className="m-0 grid list-none gap-3 p-0 sm:grid-cols-2 lg:grid-cols-3">
              {machine.data.assemblies.map((plate) => (
                <li key={plate.id}>
                  <Link
                    to={`/assemblies/${plate.id}`}
                    className="flex min-h-16 items-center gap-4 rounded-md border border-border-subtle bg-surface-card px-5 py-3 shadow-xs hover:border-green-700"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block type-h4 [font-size:var(--text-base)] text-text-strong">{plate.name}</span>
                      <span className="block type-small text-text-muted">
                        <span className="type-sku">{plate.code}</span> · {plate.part_count} {plate.part_count === 1 ? "part" : "parts"}
                      </span>
                    </span>
                    <Icon icon={ChevronRight} size={18} className="text-ink-400" />
                  </Link>
                </li>
              ))}
            </ol>
          )}
        </>
      )}
    </PageWidth>
  );
}
